/**
 * useCanAccess Hook
 *
 * Combines feature flags AND role permissions to determine access.
 *
 * Access is granted only when BOTH conditions are met:
 * 1. Feature flag is enabled (tenant-level)
 * 2. User's role has permission for the action
 *
 * Architecture:
 * - Feature Flag: Can the tenant access this feature? (organization-level)
 * - Role Permission: Can this user perform this action? (user-level)
 *
 * SECURITY: While loading, all access checks return false (deny-while-loading).
 * This prevents flash of privileged UI. Check `isLoading` first if you need
 * to show a loading state/skeleton.
 *
 * Usage:
 *   const { canAccess, canView, canCreate, canEdit, canDelete, isLoading } = useCanAccess();
 *
 *   // Handle loading state
 *   if (isLoading) {
 *     return <Skeleton />;
 *   }
 *
 *   // Check specific action
 *   if (canAccess('tools', 'create')) {
 *     // Show create button
 *   }
 *
 *   // Check view access
 *   if (canView('agent-studio')) {
 *     // Render component
 *   }
 */

import { useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { isUserAdmin } from '../utils/rbacHelpers'
import { useFeatureFlags } from '../contexts/FeatureFlagsContext'
import { useTenant } from '../contexts/TenantContext'
import { useAssumedRole } from '../contexts/AssumedRoleContext'
import {
  type FeatureFlagKey,
  getViewFlagForFeature,
} from '../constants/featureFlags'
import { resolveTreeViewFlagKey } from '../features/platform-admin/featureFlagsTree'
import type { AttachedPolicyEntry } from '../services/api/types'
import type { FeatureAction, Role } from '../types/permissions'
import {
  buildFlatPermMap,
  LEGACY_SUBFEATURE_FALLBACKS,
  findAttachedPolicyEntry,
  findScopedFeature,
  requireSubFeatureForTab,
  type ScopedFeatureKey,
  type TabKeyOf,
} from '../utils/policyUtils'

/** Legacy `role.permissions` object map used by older roles / API payloads. */
type LegacyPermissionMap = Record<string, Partial<Record<FeatureAction, boolean>> | undefined>

// =============================================================================
// TYPES
// =============================================================================

export type ActionType =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'sync'
  | 'manage_access'
  | 'marketplace'
  | 'playground'
  | 'upload'
  | 'configure'
  | 'execute'

export interface UseCanAccessReturn {
  /**
   * Check if user can access a feature with a specific action.
   * Combines feature flag check + role permission check.
   *
   * @param feature - Feature ID (e.g., 'tools', 'agent-studio')
   * @param action - Action type (e.g., 'view', 'create', 'edit')
   * @returns true if both feature flag and role permission allow access
   */
  canAccess: (feature: string, action: ActionType) => boolean

  /**
   * Check if a specific feature flag is enabled.
   * Only checks feature flag, not role permissions.
   *
   * @param flagKey - The exact flag key (e.g., 'tools_marketplace')
   * @returns true if the flag is enabled
   */
  isFlagEnabled: (flagKey: FeatureFlagKey) => boolean

  /**
   * Check if user has role permission for a feature action.
   * Only checks role, not feature flag.
   *
   * @param feature - Feature ID
   * @param action - Action type
   * @returns true if role allows the action
   */
  hasRolePermission: (feature: string, action: FeatureAction) => boolean

  /**
   * Real attached_policies / admin grant for a feature action.
   *
   * Use this for write/lifecycle UI (Archive, Delete, row checkboxes).
   * `canAccess` / `hasRolePermission` / `canDelete` stay stubs for nav.
   */
  hasGrant: (feature: string, action: FeatureAction) => boolean

  /**
   * Shorthand for canAccess(feature, 'view').
   */
  canView: (feature: string) => boolean

  /**
   * Shorthand for canAccess(feature, 'create').
   */
  canCreate: (feature: string) => boolean

  /**
   * Shorthand for canAccess(feature, 'edit').
   */
  canEdit: (feature: string) => boolean

  /**
   * Shorthand for canAccess(feature, 'delete').
   */
  canDelete: (feature: string) => boolean

  /**
   * Check sub-feature access. Combines parent feature flag + role sub-feature permission.
   * Falls back to parent action if no sub-feature override is stored.
   * Generic — works for any feature with subFeatures defined in SCOPED_FEATURES.
   *
   * @param feature - Parent feature ID (e.g., 'evals-simulations')
   * @param subFeature - Sub-feature key (e.g., 'simulations', 'agent_evals')
   * @param action - Action type (e.g., 'view', 'create')
   */
  canAccessSub: (feature: string, subFeature: string, action: FeatureAction) => boolean

  /**
   * Check UI visibility using the `view_actions` eye-toggle.
   * Works at any level: feature (sidebar/route), sub-feature (tab), or sub-tab.
   *
   * Unlike `canAccess`, this reads `view_actions` — the UI visibility flag —
   * NOT `actions.view` (API statement gate). Use this everywhere you want the
   * admin's eye-toggle to control sidebar items, route guards, tabs, and subtabs.
   *
   * @param feature    - Feature ID (e.g., 'evals-simulations')
   * @param subFeature - Optional sub-feature key (e.g., 'benchmarks', 'agent_evals')
   */
  canViewUI: (feature: string, subFeature?: string) => boolean

  /**
   * Check if feature is visible in navigation.
   * Returns true if view flag is enabled and user has view permission.
   */
  canNavigateTo: (feature: string) => boolean

  /**
   * Whether access checks are still loading.
   */
  isLoading: boolean

  /**
   * Whether user is a platform admin (bypasses most checks).
   */
  isPlatformAdmin: boolean

  /**
   * Tenant admin for data-breadth UI. False while previewing a non-admin role.
   * True while previewing the Admin role.
   */
  isAdmin: boolean
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/** Matches `evals-simulations` scoped entry in SCOPED_FEATURES / backend EVALS_SIMS_RBAC. */
const EVALS_SIMULATIONS_POLICY_SERVICE = 'evals-simulations-feature-access'

/**
 * Evals + simulations RBAC: attached_policies and legacy flat keys `evals-simulations`, `agent-evals`,
 * `agent-evals-templates`, `simulations` (underscore variants for parity with backend legacy_checker).
 */
function roleAllowsEvalsSimulations(role: Role, action: FeatureAction): boolean {
  const policy = findAttachedPolicyEntry(
    role.attached_policies as unknown as AttachedPolicyEntry[] | undefined,
    EVALS_SIMULATIONS_POLICY_SERVICE,
  )
  if (policy?.actions?.[action]) return true
  const perms = (role.permissions as unknown as LegacyPermissionMap) ?? {}
  const legacyKeys = [
    'evals-simulations',
    'agent-evals',
    'agent-evals-templates',
    'simulations',
    'evals_simulations',
    'agent_evals',
    'agent_evals_templates',
  ] as const
  for (const lk of legacyKeys) {
    if (perms[lk]?.[action] === true) return true
  }
  return false
}

/**
 * Check if user's role has permission for a feature action.
 *
 * For scoped features (e.g. skills-feature-access, agent-studio-feature-access),
 * checks attached_policies.actions first, then legacy permissions[legacyKey].
 */
/** Whether any of the user's roles grants a feature action (attached_policies + legacy map). */
export function roleHasFeatureAction(
  roles: Role[] | undefined | null,
  feature: string,
  action: FeatureAction,
): boolean {
  return checkRolePermission(roles ?? undefined, feature, action)
}

function checkRolePermission(
  roles: Role[] | undefined,
  feature: string,
  action: FeatureAction
): boolean {
  if (!roles || roles.length === 0) {
    return false
  }

  // Normalize feature ID for comparison (handle dashes vs underscores)
  const normalizedFeature = feature.toLowerCase().replace(/-/g, '_')

  // Legacy feature ids still used by some screens; same grants as evals-simulations scoped policy.
  if (
    normalizedFeature === 'agent_evals' ||
    normalizedFeature === 'agent_evals_templates' ||
    normalizedFeature === 'simulations'
  ) {
    for (const role of roles) {
      if (roleAllowsEvalsSimulations(role, action)) return true
    }
    return false
  }

  // Scoped-feature features: check attached_policies.actions first, then legacy permissions.{legacyKey}.
  const scoped = findScopedFeature(feature)
  if (scoped) {
    if (scoped.featureKey === 'evals-simulations') {
      for (const role of roles) {
        if (roleAllowsEvalsSimulations(role, action)) return true
      }
      return false
    }
    for (const role of roles) {
      const policy = findAttachedPolicyEntry(
        role.attached_policies as unknown as AttachedPolicyEntry[] | undefined,
        scoped.service,
      )
      if (policy?.actions?.[action]) return true
      const legacyPerms = (role.permissions as unknown as LegacyPermissionMap) ?? {}
      if (legacyPerms[scoped.legacyKey]?.[action] === true) return true
    }
    return false
  }

  // Smart legacy fallback for features without a scoped registry entry:
  //   - Role has ANY attached_policies → modern role; missing policy = deny.
  //   - Role has NO attached_policies   → legacy-only role; allow until the
  //     admin re-saves the role and populates attached_policies.
  // Backend remains the authoritative enforcement point — this only governs
  // sidebar/route visibility in the UI.
  for (const role of roles) {
    const policies = role.attached_policies ?? []
    if (!policies || policies.length === 0) return true
  }
  return false
}

/**
 * Check the `view_actions` UI-visibility toggle from attached_policies flat map.
 * Works for both the parent feature (subFeature omitted) and any sub-feature/sub-tab.
 *
 * `view_actions` is the eye toggle — controls nav/route/tab visibility only.
 * It is independent of `actions.view` (API statement expansion).
 *
 * - Parent check: reads `flat[fk_view]`   (= parent feature view_actions)
 * - Child check:  reads `flat[fk_sk_view]` (= sub-feature view_actions), falls back to parent
 *
 * Falls back to `checkRolePermission(..., 'view')` for roles without attached policies.
 */
function checkViewActionsUI(
  roles: Role[] | undefined,
  feature: string,
  subFeature?: string,
): boolean {
  if (!roles || roles.length === 0) return false
  const scoped = findScopedFeature(feature)
  // Non-scoped features have no eye toggle — fall back to the regular API permission check
  if (!scoped) return checkRolePermission(roles, feature, 'view')

  const fk = scoped.featureKey.replace(/-/g, '_')
  const sk = subFeature ? subFeature.replace(/-/g, '_') : null
  const targetKey = sk ? `${fk}_${sk}_view` : `${fk}_view`
  const parentViewKey = `${fk}_view`

  let anyRoleHasAttachedPolicy = false
  for (const role of roles) {
    const policies = (role.attached_policies ?? []) as unknown as AttachedPolicyEntry[]
    const hasPolicyForService = Boolean(findAttachedPolicyEntry(policies, scoped.service))
    if (!hasPolicyForService) continue
    anyRoleHasAttachedPolicy = true
    const flat = buildFlatPermMap(policies)
    if (targetKey in flat) {
      if (flat[targetKey]) return true
      continue  // explicit deny — respect stored view_actions: false
    }
    // Not re-saved since a sub-feature merge → OR of the legacy nodes it replaced.
    const legacyKeys = (sk ? LEGACY_SUBFEATURE_FALLBACKS[scoped.featureKey]?.[sk] ?? [] : [])
      .map((legacy) => `${fk}_${legacy}_view`)
      .filter((key) => key in flat)
    if (legacyKeys.length > 0) {
      if (legacyKeys.some((key) => flat[key])) return true
      continue
    }
    // No child entry → fall back to parent view_actions
    if (flat[parentViewKey]) return true
  }

  if (!anyRoleHasAttachedPolicy) {
    return checkRolePermission(roles, feature, 'view')
  }
  return false
}

// =============================================================================
// HOOK
// =============================================================================

export function useCanAccess(): UseCanAccessReturn {
  const { currentUser, isCheckingAuth } = useAuth()
  const { isEnabled, isLoading: isFlagsLoading } = useFeatureFlags()
  const { isPlatformAdmin, isLoading: isTenantLoading } = useTenant()
  const { assumedRole } = useAssumedRole()

  const isLoading = isCheckingAuth || isFlagsLoading || isTenantLoading

  // When assuming a role, build a synthetic Role object for permission checks
  const effectiveRoles: Role[] | undefined = useMemo(() => {
    if (assumedRole) {
      return [
        {
          id: assumedRole.id,
          name: assumedRole.name,
          description: assumedRole.description ?? '',
          permissions: assumedRole.permissions,
          attached_policies: assumedRole.attached_policies ?? null,
          tenant_id: null,
          created_at: 0,
          updated_at: 0,
          created_by: '',
          updated_by: '',
        } as unknown as Role,
      ]
    }
    return currentUser?.roles as Role[] | undefined
  }, [assumedRole, currentUser?.roles])

  // When assuming a role, disable platform admin bypass
  const effectiveIsPlatformAdmin = isPlatformAdmin && !assumedRole
  const assumedRoleName = (assumedRole?.name ?? '')
    .toLowerCase()
    .replace(/[\s-]/g, '_')
  const effectiveIsAdmin = assumedRole
    ? assumedRoleName === 'admin'
    : isUserAdmin(currentUser) || isPlatformAdmin

  /**
   * Access gating now lives entirely in `canViewUI` / `useTabVisibility`
   * (the `view_actions` eye-toggle). This always returns true.
   */
  const isFlagEnabled = useCallback(
    (_flagKey: FeatureFlagKey): boolean => true,
    [],
  )

  /**
   * Check role permission.
   *
   * When assuming a role, evaluates against the assumed role's permissions
   * instead of the user's real roles.
   */
  const hasRolePermission = useCallback(
    (_feature: string, _action: FeatureAction): boolean => true,
    [],
  )

  const hasGrant = useCallback(
    (feature: string, action: FeatureAction): boolean => {
      if (isLoading) return false
      if (effectiveIsPlatformAdmin || effectiveIsAdmin) return true
      return roleHasFeatureAction(effectiveRoles, feature, action)
    },
    [isLoading, effectiveIsPlatformAdmin, effectiveIsAdmin, effectiveRoles],
  )

  /**
   * Main access check combining feature flags and role permissions.
   *
   * SECURITY: While loading, returns false (deny-while-loading).
   * Consumers should check isLoading first to show loading UI if needed.
   */
  const canAccess = useCallback(
    (_feature: string, _action: ActionType): boolean => {
      // Deny while loading to avoid a flash of privileged UI; otherwise grant.
      if (isLoading) {
        return false
      }
      return true
    },
    [isLoading]
  )

  // Shorthand methods
  const canView = useCallback((feature: string) => canAccess(feature, 'view'), [canAccess])

  const canCreate = useCallback((feature: string) => canAccess(feature, 'create'), [canAccess])

  const canEdit = useCallback((feature: string) => canAccess(feature, 'edit'), [canAccess])

  const canDelete = useCallback((feature: string) => canAccess(feature, 'delete'), [canAccess])

  const canAccessSub = useCallback(
    (_feature: string, _subFeature: string, _action: FeatureAction): boolean => {
      // Deny while loading to avoid a flash of privileged UI; otherwise grant.
      if (isLoading) return false
      return true
    },
    [isLoading]
  )

  /**
   * Check `view_actions` (eye-toggle) for any level: feature, sub-feature, or sub-tab.
   * Use this for sidebar nav, route guards, and tab/subtab visibility instead of canAccess.
   */
  const canViewUI = useCallback(
    (feature: string, subFeature?: string): boolean => {
      if (isLoading) return false

      // Platform admins bypass scoped eye-toggle / flag gates (same as backend
      // /auth/check-access and FeatureRBAC.authorize). Assumed-role mode still
      // evaluates the assumed role's attached_policies below.
      if (effectiveIsPlatformAdmin) return true

      const viewFlag = getViewFlagForFeature(feature)
      if (viewFlag && !isEnabled(viewFlag)) return false

      // Sub-feature view flags are AND-gated into attached_policies by
      // GET /auth/me (flag_gate). Do not re-check isEnabled(treeViewFlag) here:
      // createDefaultFlags seeds new keys as false, which false-denies new
      // children (e.g. ontology.build_with_ai) before/without the API payload.
      return checkViewActionsUI(effectiveRoles, feature, subFeature)
    },
    [isLoading, isEnabled, effectiveRoles, effectiveIsPlatformAdmin]
  )

  const canNavigateTo = useCallback((feature: string) => canViewUI(feature), [canViewUI])

  return useMemo(
    () => ({
      canAccess,
      canAccessSub,
      canViewUI,
      isFlagEnabled,
      hasRolePermission,
      hasGrant,
      canView,
      canCreate,
      canEdit,
      canDelete,
      canNavigateTo,
      isLoading,
      isPlatformAdmin: effectiveIsPlatformAdmin,
      isAdmin: effectiveIsAdmin,
    }),
    [
      canAccess,
      canAccessSub,
      canViewUI,
      isFlagEnabled,
      hasRolePermission,
      hasGrant,
      canView,
      canCreate,
      canEdit,
      canDelete,
      canNavigateTo,
      isLoading,
      effectiveIsPlatformAdmin,
      effectiveIsAdmin,
    ]
  )
}

// =============================================================================
// CONVENIENCE HOOKS
// =============================================================================

/**
 * Hook to check if user can access a specific feature.
 * Returns an object with all action permissions for that feature.
 */
export function useFeatureAccess(feature: string): {
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  isLoading: boolean
} {
  const { canAccess, isLoading } = useCanAccess()

  return useMemo(
    () => ({
      canView: canAccess(feature, 'view'),
      canCreate: canAccess(feature, 'create'),
      canEdit: canAccess(feature, 'edit'),
      canDelete: canAccess(feature, 'delete'),
      isLoading,
    }),
    [canAccess, feature, isLoading]
  )
}

/**
 * Hook to check a specific flag directly.
 * Useful for granular checks like 'tools_marketplace'.
 *
 * SECURITY: Returns false while loading (deny-while-loading).
 * Use `useCanAccess().isLoading` to show loading UI if needed.
 */
export function useFlagCheck(flagKey: FeatureFlagKey): boolean {
  const { isFlagEnabled, isLoading } = useCanAccess()

  // While loading, deny access for security
  if (isLoading) {
    return false
  }

  return isFlagEnabled(flagKey)
}

/**
 * Hook to get view-access for all sub-features of a parent feature.
 * Returns a map of { subFeatureKey: boolean } for the 'view' action.
 * Consumers use this to show/hide tabs without hardcoding feature keys.
 */
export function useSubFeatureViewMap(feature: string): Record<string, boolean> {
  const { canViewUI, isLoading } = useCanAccess()
  const scoped = findScopedFeature(feature)

  return useMemo(() => {
    if (!scoped?.subFeatures) return {}
    const result: Record<string, boolean> = {}
    for (const subKey of Object.keys(scoped.subFeatures)) {
      result[subKey] = !isLoading && canViewUI(feature, subKey)
    }
    return result
  }, [scoped, canViewUI, feature, isLoading])
}

/**
 * Common hook for tab/subtab visibility in any feature page.
 *
 * Centralises the pattern:  tabKey → subFeatureKey → canViewUI(feature, subKey)
 * so individual pages don't reimplement it.
 *
 * Usage (preferred — pure dot-access, IDE shows every valid key, no string literals):
 *   const { tabs } = useTabVisibility('agent-studio')
 *   {tabs.auto_tune && <AutoTuneButton />}
 *   {tabs.build_with_ai && <BuildWithAiButton />}
 *
 * Usage (functional form — for filtering arrays or dynamic keys):
 *   const { filterTabs } = useTabVisibility('evals-simulations')
 *   const visibleTabs = filterTabs(ALL_MAIN_TABS)
 */
export function useTabVisibility<F extends ScopedFeatureKey>(featureKey: F): {
  /** Typed boolean map. `tabs.<key>` is true when the user can see that sub-feature. */
  tabs: { readonly [K in TabKeyOf<F>]: boolean }
  canViewTab: (tabKey: TabKeyOf<F>) => boolean
  filterTabs: <T extends TabKeyOf<F>>(tabs: readonly T[]) => T[]
  /**
   * Resolve a tab to the SAME view flag the visibility layer uses, so lifecycle
   * stage chips read the correct flag. Prefers the nested tree flag
   * (`{feature}_{...path}_view`) the admin manages, falling back to the flat
   * `{subKey}_view` / `{feature}_view` meta flag. Pass the result to
   * `TabWithBadge`/`FeatureStageBadge`. Returns undefined when no flag maps.
   */
  getTabViewFlag: (tabKey: TabKeyOf<F>) => string | undefined
  /**
   * Access state still loading — pages should not flash the lock banner
   * during this window. Pair this with their own `visibleTabs.length === 0`
   * to decide when to render <PermissionDenied />.
   */
  isLoading: boolean
} {
  const { canViewUI, isLoading } = useCanAccess()

  const canViewTab = useCallback(
    (tabKey: TabKeyOf<F>): boolean => {
      if (isLoading) return false
      const subKey = requireSubFeatureForTab(featureKey, tabKey as string)
      return canViewUI(featureKey, subKey)
    },
    [canViewUI, featureKey, isLoading],
  )

  const filterTabs = useCallback(
    <T extends TabKeyOf<F>>(tabs: readonly T[]): T[] => tabs.filter((tab) => canViewTab(tab)),
    [canViewTab],
  )

  const getTabViewFlag = useCallback(
    (tabKey: TabKeyOf<F>): string | undefined => {
      const subKey = requireSubFeatureForTab(featureKey, tabKey as string)
      // Tree flag first (what the admin sets stage on), then flat meta fallbacks.
      return (
        resolveTreeViewFlagKey(featureKey, subKey) ??
        getViewFlagForFeature(subKey) ??
        getViewFlagForFeature(featureKey)
      )
    },
    [featureKey],
  )

  // Precompute a dot-accessible boolean map covering every valid key for this feature
  // (sub-feature keys + tabKey aliases + parentTab groups). Drives the `tabs.<key>` API.
  const tabs = useMemo(() => {
    const map: Record<string, boolean> = {}
    const feat = findScopedFeature(featureKey)
    if (feat?.subFeatures) {
      for (const [subKey, meta] of Object.entries(feat.subFeatures)) {
        const val = canViewTab(subKey as TabKeyOf<F>)
        map[subKey] = val
        if (meta.tabKey) map[meta.tabKey] = val
        if (meta.parentTab && !(meta.parentTab in map)) {
          map[meta.parentTab] = canViewTab(meta.parentTab as TabKeyOf<F>)
        }
      }
    }
    return map as { [K in TabKeyOf<F>]: boolean }
  }, [canViewTab, featureKey])

  return useMemo(
    () => ({ tabs, canViewTab, filterTabs, getTabViewFlag, isLoading }),
    [tabs, canViewTab, filterTabs, getTabViewFlag, isLoading],
  )
}

export default useCanAccess
